import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';
import { Copy, Plus, Trash2, Search, Edit } from 'lucide-react';
import { format } from 'date-fns';

export function ReferralManagement({ apiPrefix = 'admin' }: { apiPrefix?: 'admin' | 'developer' }) {
  const [referralCodes, setReferralCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // @ts-ignore
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newCode, setNewCode] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [discount, setDiscount] = useState('0');
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [users, setUsers] = useState<any[]>([]);

  const fetchReferralCodes = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/referral`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });
      const data = await res.json();
      if (data.ok) {
        setReferralCodes(data.data);
      } else {
        toast.error(data.error || 'Failed to fetch referral codes');
      }
    } catch (err: any) {
      toast.error(err.message || 'Error fetching referral codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/${apiPrefix}/users?limit=100`, {
        headers: { 'Authorization': `Bearer ${session?.access_token}` }
      });
      const data = await res.json();
      if (data.ok) {
        setUsers(data.data.users || data.data);
      }
    } catch (error) {
      console.error('Error fetching users for referral code owner:', error);
    }
  };

  useEffect(() => {
    fetchReferralCodes();
    fetchUsers();
  }, []);

  const handleSave = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = editingId ? `${API_URL}/api/referral/${editingId}` : `${API_URL}/api/referral`;
      const method = editingId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: newCode,
          owner_id: ownerId,
          valid_until: validUntil ? new Date(validUntil).toISOString() : null,
          discount_percentage: parseInt(discount) || 0,
          is_active: isActive
        })
      });
      const data = await res.json();
      if (data.ok) {
        toast.success(`Referral code ${editingId ? 'updated' : 'created'} successfully`);
        setIsDialogOpen(false);
        setEditingId(null);
        setNewCode('');
        setOwnerId('');
        setUserSearch('');
        setValidUntil('');
        setIsActive(true);
        setDiscount('0');
        fetchReferralCodes();
      } else {
        toast.error(data.error || `Failed to ${editingId ? 'update' : 'create'} code`);
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleEditClick = (code: any) => {
    setEditingId(code.id);
    setNewCode(code.code || '');
    setOwnerId(code.owner_id || '');
    setUserSearch(code.owner?.full_name || code.owner?.email || '');
    setValidUntil(code.valid_until ? (new Date(code.valid_until).toISOString().split('T')[0] || '') : '');
    setIsActive(code.is_active !== false);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this code?')) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/referral/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });
      const data = await res.json();
      if (data.ok) {
        toast.success('Deleted successfully');
        fetchReferralCodes();
      } else {
        toast.error(data.error);
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const generateLink = (code: string) => {
    return `${window.location.origin}/?ref=${code}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard');
  };

  const filteredUsers = users.filter(u => 
    (u.full_name?.toLowerCase() || '').includes(userSearch.toLowerCase()) || 
    (u.email?.toLowerCase() || '').includes(userSearch.toLowerCase())
  );

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-xl">Referral Management</CardTitle>
          <CardDescription>Create and track referral codes</CardDescription>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => {
              setEditingId(null);
              setNewCode('');
              setOwnerId('');
              setUserSearch('');
              setValidUntil('');
            }}>
              <Plus className="w-4 h-4 mr-2" /> New Code
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Referral Code' : 'Create New Referral Code'}</DialogTitle>
              <DialogDescription>{editingId ? 'Modify referral code settings.' : 'Generate a new code to give to an influencer or user.'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Code (Unique)</label>
                <Input placeholder="e.g. BDM2026" value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} disabled={!!editingId} />
              </div>
              <div className="space-y-2 relative">
                <label className="text-sm font-medium">Owner (User)</label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search user name or email..." 
                    className="pl-9"
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setShowUserDropdown(true);
                      if (ownerId) setOwnerId('');
                    }}
                    onFocus={() => setShowUserDropdown(true)}
                    onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                  />
                </div>
                {showUserDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-background border border-input rounded-md shadow-md max-h-48 overflow-y-auto">
                    {filteredUsers.map(u => (
                      <div 
                        key={u.id} 
                        className="px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer flex flex-col"
                        onMouseDown={() => {
                          setOwnerId(u.id);
                          setUserSearch(u.full_name || u.email);
                          setShowUserDropdown(false);
                        }}
                      >
                        <span className="font-medium">{u.full_name || 'No Name'}</span>
                        <span className="text-xs text-muted-foreground">{u.email}</span>
                      </div>
                    ))}
                    {filteredUsers.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No users found.</div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Valid Until (Optional)</label>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      id="noExpiration"
                      checked={!validUntil}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValidUntil('');
                        } else {
                          const tmr = new Date();
                          tmr.setDate(tmr.getDate() + 30);
                          setValidUntil(tmr.toISOString().split('T')[0] || '');
                        }
                      }}
                      className="w-3.5 h-3.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <label htmlFor="noExpiration" className="text-xs text-muted-foreground cursor-pointer select-none">
                      No Expiration (Lifetime)
                    </label>
                  </div>
                </div>
                <Input 
                  type="date" 
                  value={validUntil} 
                  onChange={(e) => setValidUntil(e.target.value)} 
                  disabled={!validUntil}
                  className="w-full dark:[&::-webkit-calendar-picker-indicator]:invert disabled:opacity-50"
                />
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <input 
                  type="checkbox" 
                  id="isActive" 
                  checked={isActive} 
                  onChange={(e) => setIsActive(e.target.checked)} 
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isActive" className="text-sm font-medium cursor-pointer">
                  Active (Code can be used)
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave}>{editingId ? 'Save Changes' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center p-8">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Clicks</TableHead>
                  <TableHead>Claims (Checkout)</TableHead>
                  <TableHead>Active Subscriptions</TableHead>
                  <TableHead>Pending Subscriptions</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referralCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      No referral codes found
                    </TableCell>
                  </TableRow>
                ) : (
                  referralCodes.map((code) => (
                    <TableRow key={code.id}>
                      <TableCell className="font-mono font-bold text-blue-400">
                        {code.code}
                      </TableCell>
                      <TableCell>
                        {code.is_active === false ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">Inactive</span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Active</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {code.owner?.full_name || 'Unknown'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {code.owner?.email}
                        </div>
                      </TableCell>
                      <TableCell>{code.stats?.clicks}</TableCell>
                      <TableCell>{code.stats?.checkouts}</TableCell>
                      <TableCell>
                        <span className="text-green-500 font-bold">{code.stats?.successful_subscriptions}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-yellow-600 font-bold">{code.stats?.pending_subscriptions || 0}</span>
                      </TableCell>
                      <TableCell>
                        {code.valid_until ? format(new Date(code.valid_until), 'dd MMM yyyy') : 'Forever'}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => copyToClipboard(generateLink(code.code))} title="Copy Link">
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEditClick(code)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(code.id)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
